<template>
  <div>
    <!-- Reservation detail view -->
    <reservation-detail
      v-if="selected_reservation_id"
      :reservationId="selected_reservation_id"
      @close="selected_reservation_id = null"
    />

    <!-- Import detail view -->
    <v-card v-else>
      <v-card-title class="d-flex align-center py-4">
        <v-btn icon @click="$emit('close')" class="mr-2">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span>{{ $t("import_report") }} - {{ item.pms }}</span>
      </v-card-title>
      <v-card-text>
        <!-- Status -->
        <v-row class="mb-2">
          <v-col cols="12">
            <v-alert :type="importAlertType" dense outlined class="mb-0">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <strong>{{ $t("status") }}:</strong>
                  {{ importStatusLabel }}
                </div>
                <div
                  v-if="item.report && item.report.import_finished_at"
                  class="text-caption"
                >
                  {{ item.report.import_finished_at }}
                </div>
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <!-- Import info -->
        <v-row class="mb-2">
          <v-col cols="12" sm="6">
            <div class="text-caption grey--text">{{ $t("data_of") }}</div>
            <div>{{ item.import_date | formatDate }}</div>
          </v-col>
          <v-col cols="12" sm="6">
            <div class="text-caption grey--text">{{ $t("imported_on") }}</div>
            <div>
              {{ item.date | formatDateFull }} {{ $t("at") }}
              {{ item.date | formatTime }}
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Reservations summary -->
        <div v-if="item.report">
          <div class="text-subtitle-1 font-weight-bold mb-3">
            {{ $t("reservations") }}
          </div>
          <v-row>
            <v-col cols="4" class="text-center">
              <div class="text-h5 font-weight-bold">
                {{ item.report.reservations_total || 0 }}
              </div>
              <div class="text-caption grey--text">Total</div>
            </v-col>
            <v-col cols="4" class="text-center">
              <div class="text-h5 font-weight-bold green--text">
                {{ item.report.reservations_ok_count || 0 }}
              </div>
              <div class="text-caption grey--text">OK</div>
            </v-col>
            <v-col cols="4" class="text-center">
              <div class="text-h5 font-weight-bold red--text">
                {{ item.report.reservations_error_count || 0 }}
              </div>
              <div class="text-caption grey--text">{{ $t("errors") }}</div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Reservations detail table with pagination -->
          <div class="text-subtitle-2 font-weight-bold mb-2">
            <v-icon small color="secondary" class="mr-1"
              >mdi-format-list-bulleted</v-icon
            >
            {{ $t("reservations") }} ({{ item.report.reservations_total || 0 }})
          </div>

          <v-data-table
            :headers="reservation_headers"
            :items="reservations"
            :server-items-length="server_total"
            :options.sync="table_options"
            :loading="loading"
            :footer-props="{
              'items-per-page-options': [25, 50, 100],
              'items-per-page-text': $t('rows_per_page')
            }"
            dense
            class="elevation-0"
            :no-data-text="$t('no_data')"
            @click:row="openReservationDetail"
            :item-class="() => 'a-datatable-row'"
          >
            <template v-slot:[`item.status`]="{ item: res }">
              <v-chip
                x-small
                :color="
                  res.status === 'ok' || res.status === 'confirmed'
                    ? 'green'
                    : res.status === 'error'
                    ? 'red'
                    : 'grey'
                "
                dark
              >
                {{ res.status }}
              </v-chip>
            </template>
          </v-data-table>

          <!-- Reservations errors list -->
          <div
            v-if="
              item.report.reservations_errors &&
                item.report.reservations_errors.length > 0
            "
            class="mt-4"
          >
            <div class="text-subtitle-2 font-weight-bold mb-2">
              <v-icon small color="red" class="mr-1">mdi-alert-circle</v-icon>
              {{ $t("errors") }} ({{ item.report.reservations_errors.length }})
            </div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">#</th>
                    <th class="text-left">{{ $t("reservation") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(resErr, index) in item.report.reservations_errors"
                    :key="'err-' + index"
                  >
                    <td>{{ index + 1 }}</td>
                    <td>{{ resErr }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </div>

        <!-- Pending state -->
        <div v-if="item.status === 'pending'" class="text-center py-4">
          <v-progress-circular
            indeterminate
            color="secondary"
            class="mb-3"
          ></v-progress-circular>
          <div>{{ $t("importing_data") }}...</div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ReservationDetail from "@/components/reservations/ReservationDetail.vue";

export default {
  components: { ReservationDetail },
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    reservations: [],
    loading: false,
    server_total: 0,
    selected_reservation_id: null,
    table_options: {
      page: 1,
      itemsPerPage: 25,
      sortBy: [],
      sortDesc: []
    }
  }),
  computed: {
    reservation_headers() {
      return [
        {
          text: "ID",
          align: "left",
          sortable: true,
          value: "id"
        },
        {
          text: "PMS ID",
          align: "left",
          sortable: true,
          value: "pms_id"
        },
        {
          text: this.$t("status"),
          align: "left",
          sortable: true,
          value: "status"
        },
        {
          text: "Check-in",
          align: "left",
          sortable: true,
          value: "check_in"
        },
        {
          text: "Check-out",
          align: "left",
          sortable: true,
          value: "check_out"
        },
        {
          text: "Pax",
          align: "left",
          sortable: true,
          value: "pax"
        },
        {
          text: this.$t("intermediary"),
          align: "left",
          sortable: true,
          value: "intermediate"
        }
      ];
    },
    importAlertType() {
      if (this.item.report && this.item.report.import_status === "ok") {
        return "success";
      }
      if (this.item.status === "pending") {
        return "info";
      }
      return "error";
    },
    importStatusLabel() {
      if (this.item.report && this.item.report.import_status === "ok") {
        return this.$t("processed");
      }
      return this.$t(this.item.status);
    },
    ...mapGetters({
      current_hotel: "current_hotel"
    })
  },
  methods: {
    openReservationDetail(row) {
      this.selected_reservation_id = row.id;
    },
    fetchReservations() {
      this.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.table_options;
      this.$store
        .dispatch("getImportReservations", {
          hotel_id: this.current_hotel.id,
          import_task_id: this.item.import_task_id,
          page: page,
          per_page: itemsPerPage,
          sort_by: sortBy.length > 0 ? sortBy[0] : null,
          sort_desc: sortDesc.length > 0 ? sortDesc[0] : null
        })
        .then(response => {
          this.reservations = response.data || [];
          this.server_total = response.total || 0;
          this.loading = false;
        })
        .catch(() => {
          this.reservations = [];
          this.server_total = 0;
          this.loading = false;
        });
    }
  },
  watch: {
    table_options: {
      handler() {
        this.fetchReservations();
      },
      deep: true
    }
  },
  mounted() {
    this.fetchReservations();
  }
};
</script>
